import type { ComponentType, CSSProperties, FC, JSX, LazyExoticComponent, ReactNode } from 'react'
import { lazy, Suspense, useState } from 'react'
import { Button } from '../primitives/button.js'
import { BlockBoundary, Loading, Placeholder } from './lazy-block-states.js'
import styles from './lazy-block.module.css'

export interface LazyBlockOptions<P> {
  /** The finished block's size, from its props: a drawing at its stored size, a PDF page frame */
  placeholder(props: P): CSSProperties
  /**
   * Wraps the loading and failed states. An editor element passes one that renders its Slate `attributes`
   * and `children` around the content, which Slate needs whatever the block shows.
   */
  frame?(props: P, content: ReactNode): ReactNode
}

/**
 * A block whose code is loaded on first render rather than with the app (GH #2408), so a plugin's index
 * stays a small descriptor and its heavy library arrives only for a song that uses it.
 *
 * - While the code loads, a placeholder holds the finished block's size: no layout shift.
 * - `Loading…` appears only after 300 ms.
 * - If the chunk fails (offline, a deploy mid-session), the block says "This block couldn't load." with
 *   Retry, inside the same box. Retry imports it again: `React.lazy` keeps a rejected import for good, so
 *   each attempt is a fresh `lazy()`, shared by every block of this type.
 */
export const lazyBlock = <P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
  options: LazyBlockOptions<P>
): FC<P> => {
  const { placeholder, frame = (_props: P, content: ReactNode) => content } = options
  const attempts: LazyExoticComponent<ComponentType<P>>[] = []
  const lazyFor = (attempt: number) => {
    attempts[attempt] ??= lazy(load)
    return attempts[attempt]
  }

  const LazyBlock: FC<P> = props => {
    const [attempt, setAttempt] = useState(0)
    const Loaded = lazyFor(attempt)
    const style = placeholder(props)
    return (
      <BlockBoundary
        key={attempt}
        renderFailed={() =>
          frame(
            props,
            <Placeholder style={style}>
              <span className={styles.caption}>This block couldn&apos;t load.</span>
              <Button size="small" onClick={() => setAttempt(current => current + 1)}>
                Retry
              </Button>
            </Placeholder>
          )
        }
      >
        <Suspense fallback={frame(props, <Loading style={style} />)}>
          <Loaded {...(props as P & JSX.IntrinsicAttributes)} />
        </Suspense>
      </BlockBoundary>
    )
  }
  return LazyBlock
}
